import { apiGet, apiPost, apiPatch, apiDelete } from '@/api/client';
import {
  normalizeBuildingDocuments,
  type BuildingDocument,
} from '@/features/properties/lib/documents-normalize';
import type { Building } from '@/types/domain';

export interface ShareReportPreview {
  subject: string;
  messageHtml: string;
  /** Template body the share sheet edits. The email shell stays on send. */
  messageBody?: string;
  cc: string;
  /** FR-294 — Share Report default CC (Admin > Email CC Policy). Older APIs omit it. */
  defaultCc?: string[];
  templateName: string;
}

export type SharePreviewContext = 'measurement-report' | 'tenant-listing';

export interface ShareRecipientLookup {
  userId: number;
  email: string;
  role: string | null;
  roleName: string;
  active: boolean;
  registered: boolean;
  isLimitedUser: boolean;
}

export interface ShareRecipientLookupResponse {
  user: ShareRecipientLookup | null;
  companyByDomain: {
    companyId: number;
    companyName: string;
  } | null;
}

export type ShareReportRecipientPayload = {
  email: string;
  role?: 'CompanyAdministrator' | 'CompanyManager' | 'User';
  companyId?: number | null;
  companyName?: string | null;
};

export interface BuildingMember {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  roleName: string | null;
  companyName: string | null;
  accessLevel: string | null;
  status: string | null;
  /** Account Active flag (`users.active`). Not the per-property UPD status. */
  active?: boolean | null;
  registered: boolean | null;
  addedDate: string | null;
  lastAccessed: string | null;
}

// ─── Stacking Plan (PRD §05) ─────────────────────────────────────

export interface StackingPlanTenant {
  tenantId: number;
  tenantName: string;
  floorId: number;
  suite: string;
  rentableArea: number;
  clientRentableArea: number;
  leaseExpiration: string | null;
  isOccupied: boolean;
  monthToMonth?: boolean;
  noLed?: boolean;
  status: string;
}

export interface StackingPlanFloor {
  floorId: number;
  floorNumber: number;
  rentableArea: number;
  usableArea: number;
  clientRentableArea: number;
  occupiedArea: number;
  clientOccupiedArea: number;
  vacantArea: number;
  occupancy: number;
  floorOrder: number;
  tenants: StackingPlanTenant[];
}

export interface StackingPlanReportVersion {
  reportId: number;
  version: number;
  isActive: number;
  reportDate: string | null;
  status: string | null;
}

export interface StackingPlanDetailResponse {
  floors: StackingPlanFloor[];
  reportVersions: StackingPlanReportVersion[];
  currentReportId: number | null;
}

/** Build the optional Idempotency-Key axios config (drops the header
 *  when no key is supplied so we don't send `undefined` as a value). */
function withIdempotency(key?: string) {
  return key ? { headers: { 'Idempotency-Key': key } } : undefined;
}

/** Contact-type ("Select Title") option for the Contact Information editor. */
export interface ContactTypeOption {
  contactTypeId: number;
  name: string | null;
  code: string | null;
}

/** A candidate user for a contact dropdown. */
export interface ContactUserOption {
  userId: number;
  name: string | null;
  email: string | null;
  companyId: number | null;
  companyName: string | null;
}

/** Contact-user candidates: SSI staff (Project Manager), salespeople (Sales Rep), and client users. */
export interface BuildingContactUsers {
  ssiStaff: ContactUserOption[];
  salesStaff: ContactUserOption[];
  contactUsers: ContactUserOption[];
}

export const buildingsApi = {
  /** Get properties with pagination/filtering via the BFF */
  getProperties(params: {
    take: number;
    skip: number;
    userId?: number;
    search?: string;
    companyId?: number;
    companyIds?: number[];
    buildingTypeIds?: number[];
    reportTypeIds?: number[];
    subscriptionIds?: number[];
    sortBy?: string;
    sortDirection?: string;
    columnQueries?: Record<string, string>;
  }): Promise<unknown> {
    const {
      take,
      skip,
      companyIds,
      buildingTypeIds,
      reportTypeIds,
      subscriptionIds,
      columnQueries,
      ...rest
    } = params;
    const hasColumnQueries =
      columnQueries != null
      && Object.values(columnQueries).some((value) => value.trim().length > 0);
    return apiGet<unknown>(`/company/PropertyList/${take}/${skip}`, {
      ...rest,
      companyIds: companyIds?.length ? companyIds.join(',') : undefined,
      buildingTypeIds: buildingTypeIds?.length ? buildingTypeIds.join(',') : undefined,
      reportTypeIds: reportTypeIds?.length ? reportTypeIds.join(',') : undefined,
      subscriptionIds: subscriptionIds?.length ? subscriptionIds.join(',') : undefined,
      columnQueries: hasColumnQueries ? JSON.stringify(columnQueries) : undefined,
    });
  },

  /** Report type lookup for property list filters (legacy `/portfolio/reportstatuses`). */
  getReportStatuses(): Promise<
    Array<{
      reportStatusId: number;
      description: string | null;
      statusCode: string | null;
      phrase: string | null;
    }>
  > {
    return apiGet('/portfolio/reportstatuses');
  },

  /** Building type lookup for property list filters. */
  getBuildingTypes(): Promise<
    Array<{
      buildingTypeId: number;
      typeName: string | null;
    }>
  > {
    return apiGet('/portfolio/buildingtypes');
  },

  updateReportView(
    buildingId: number,
    data: { ssiReportReviewId?: number | null; clientViewableReportId?: number | null },
  ): Promise<unknown> {
    return apiPatch<unknown>(`/portfolio/building/${buildingId}/report-view`, data);
  },

  /** Contact-type ("Select Title") lookup for the Contact Information editor. */
  getContactTypes(): Promise<ContactTypeOption[]> {
    return apiGet<ContactTypeOption[]>('/portfolio/contacttypes');
  },

  /** Candidate users for a building's contact pickers (SSI staff + client users). */
  getBuildingContactUsers(buildingId: number): Promise<BuildingContactUsers> {
    return apiGet<BuildingContactUsers>(
      `/portfolio/building/${buildingId}/contact-users`,
    );
  },

  /**
   * Replace a building's Contact Information set (PM / Sales Rep / Contract
   * Contact + additional). Full desired set; an empty array clears contacts.
   */
  updateContacts(
    buildingId: number,
    data: { contacts: Array<{ contactTypeId: number; userId: number }> },
  ): Promise<unknown> {
    return apiPatch<unknown>(`/portfolio/building/${buildingId}/contacts`, data);
  },

  shareReport(
    buildingId: number,
    reportId: number,
    recipients: ShareReportRecipientPayload[],
    options?: {
      subject?: string;
      messageHtml?: string;
      cc?: string;
      /**
       * FR-294 — the edited CC list; SSI sharers only. Omit it and the server copies the
       * Share Report defaults; an empty list means every default was removed.
       */
      ccEmails?: string[];
      isTenantListing?: boolean;
    },
  ): Promise<{ shared: number; skipped: string[] }> {
    return apiPost<{ shared: number; skipped: string[] }>(
      `/buildings/${buildingId}/reports/${reportId}/share`,
      {
        recipients,
        subject: options?.subject,
        messageHtml: options?.messageHtml,
        cc: options?.cc,
        ccEmails: options?.ccEmails,
        isTenantListing: options?.isTenantListing,
      },
    );
  },

  getShareReportPreview(
    buildingId: number,
    reportId: number,
    context: SharePreviewContext = 'measurement-report',
    recipientEmail?: string,
  ): Promise<ShareReportPreview> {
    const params: { context?: SharePreviewContext; recipientEmail?: string } = {};
    if (context === 'tenant-listing') params.context = context;
    if (recipientEmail) params.recipientEmail = recipientEmail;
    return apiGet<ShareReportPreview>(
      `/buildings/${buildingId}/reports/${reportId}/share-preview`,
      Object.keys(params).length > 0 ? params : undefined,
    );
  },

  lookupShareRecipient(
    buildingId: number,
    email: string,
  ): Promise<ShareRecipientLookupResponse> {
    return apiGet<ShareRecipientLookupResponse>(
      `/buildings/${buildingId}/share-recipient-lookup`,
      { email },
    );
  },

  /** Filter properties (POST with filter body) */
  filterProperties(filters: unknown): Promise<unknown> {
    return apiPost<unknown>('/company/FilterPropertyListSearch', filters);
  },

  /** Get property detail */
  getPropertyDetail(userId: number, buildingId: number, reportId?: number): Promise<unknown> {
    const url = reportId
      ? `/portfolio/propertyDetail/${userId}/${buildingId}/${reportId}`
      : `/portfolio/propertyDetail/${userId}/${buildingId}`;
    return apiGet<unknown>(url);
  },

  /**
   * Get building info.
   *
   * DEF-323: pass `{ silent: true }` for background/best-effort prefill lookups
   * (e.g. Create Order's property-details autofill) where a 403 on a
   * BASE-LIMITED building is an expected, non-actionable outcome — the caller
   * falls back to manual entry instead of surfacing the generic permission toast.
   */
  getBuilding(buildingId: number, reportId?: number, opts?: { silent?: boolean }): Promise<Building> {
    const url = reportId
      ? `/portfolio/building/${buildingId}/${reportId}`
      : `/portfolio/building/${buildingId}`;
    return apiGet<Building>(url, undefined, opts?.silent ? { _suppressPermissionToast: true } : undefined);
  },

  /**
   * Add a new property.
   *
   * QW-7 / PB3 — accepts an optional `idempotencyKey` (caller mints a
   * UUID per submit). The key is forwarded as the `Idempotency-Key`
   * header per `architecture.mdc` §3.4 / `scaling.mdc` §11 so the
   * server can dedupe accidental double-submits (laggy network +
   * impatient click). When the server-side idempotency middleware is
   * not yet wired on the legacy `/portfolio/AddProperty` route the
   * header is simply ignored — there is no client breakage today and
   * dedupe activates automatically once the route opts in.
   */
  addProperty(data: unknown, idempotencyKey?: string): Promise<unknown> {
    return apiPost<unknown>('/portfolio/AddProperty', data, withIdempotency(idempotencyKey));
  },

  /** Partial-update merge for TruSpace-native building fields (PATCH `/portfolio/building/:id`). */
  updateProperty(
    buildingId: number,
    data: Record<string, unknown>,
    idempotencyKey?: string,
  ): Promise<Building> {
    return apiPatch<Building>(
      `/portfolio/building/${buildingId}`,
      data,
      withIdempotency(idempotencyKey),
    );
  },

  /**
   * Delete a TruDoc attached to a building.
   *
   * QW-4 / §3.B / Bug 1 — wires the trash-can affordance on the
   * Documents tab. Server-side soft delete on `BuildingFloorFile`
   * (no mirror equivalent today; lives on Neon).
   */
  deleteDocument(buildingId: number, documentId: number): Promise<unknown> {
    return apiDelete<unknown>(`/buildings/${buildingId}/documents/${documentId}`);
  },

  /** TruDocs list (PRD §09) — normalizes legacy GetBuildingDocuments rows. */
  listDocuments(buildingId: number): Promise<BuildingDocument[]> {
    return apiGet<unknown>(`/buildings/GetBuildingDocuments/${buildingId}`).then(normalizeBuildingDocuments);
  },

  /** Stacking plan floors + tenants + report versions (PRD §05). */
  getStackingPlanDetail(buildingId: number, reportId?: number | null): Promise<StackingPlanDetailResponse> {
    const query = reportId != null ? `?reportId=${reportId}` : '';
    return apiGet<StackingPlanDetailResponse>(
      `/buildings/${buildingId}/stacking-plan-detail${query}`,
    );
  },

  /** Property members roster (T-08 Members tab). */
  listMembers(buildingId: number): Promise<BuildingMember[]> {
    return apiGet<BuildingMember[]>(`/buildings/${buildingId}/members`);
  },

  /** Invite or grant a member on a property (PRD §08 / T-08). */
  inviteMember(
    buildingId: number,
    payload: { userId?: number; email?: string; accessLevel: string },
  ): Promise<BuildingMember> {
    return apiPost<BuildingMember>(`/buildings/${buildingId}/members`, payload);
  },

  /** Update a member's property access level. */
  updateMember(
    buildingId: number,
    userId: number,
    payload: { accessLevel: string },
  ): Promise<BuildingMember> {
    return apiPatch<BuildingMember>(`/buildings/${buildingId}/members/${userId}`, payload);
  },

  /** Remove a member from this property (SSI-only). */
  removeMember(buildingId: number, userId: number): Promise<{ removed: boolean }> {
    return apiDelete<{ removed: boolean }>(`/buildings/${buildingId}/members/${userId}`);
  },

  /** Deactivate a user account account-wide (SSI-only). */
  deactivateMember(buildingId: number, userId: number): Promise<{ deactivated: boolean }> {
    return apiPost<{ deactivated: boolean }>(
      `/buildings/${buildingId}/members/${userId}/deactivate`,
      {},
    );
  },
};
